import { 
  Component, 
  OnInit, 
  OnDestroy, 
  ViewChild, 
  ElementRef, 
  Renderer2, 
  ChangeDetectionStrategy, 
  ChangeDetectorRef, 
  AfterViewInit
} from '@angular/core';

import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, takeUntil, auditTime } from 'rxjs/operators';

import { Select } from '@ngxs/store';

import { isNil } from 'lodash';

import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { ViewState } from '../../state/view.state';

import { VisibilityLayersService } from 'src/app/story/visibility-layers.service';

@Component({
  selector: 'app-view-layers-slider',
  templateUrl: './view-layers-slider.component.html',
  styleUrls: ['./view-layers-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ViewLayersSliderComponent implements OnInit, AfterViewInit, OnDestroy {

  // ---------- Child Elements ----------

  @ViewChild('slider', { static: true})
  slider: ElementRef;

  @ViewChild('track', { static: true})
  track: ElementRef;

  @ViewChild('bar', { static: true})
  bar: ElementRef;

  @ViewChild('handle', { static: true})
  handle: ElementRef;

  @Select(StoryChaptersState.getSelectedChapterCurrentLayers)
  selectedChapterCurrentLayers$: Observable<any>;

  @Select(StoryChaptersState.getSelectedChapterCurrentLayersIndex)
  selectedChapterCurrentLayersIndex$: Observable<any>;

  @Select(StoryChaptersState.getSelectedChapterCurrentLayersPosition)
  selectedChapterCurrentLayersPosition$: Observable<any>;

  @Select(ViewState.getCanTouch)
  canTouch$: Observable<boolean>;

  ngUnsubscribe = new Subject();

  private currentLayers = [];

  private fullOffset = 0;
  private dragMove = new Subject();

  dragPosition = {x: 0, y: 0};
  currentLayerName = '';

  constructor(
    private renderer: Renderer2,
    private visibilityLayersService: VisibilityLayersService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.initLayersObserver();
    this.initIndexPositionObserver();
    this.initDragMoveObserver();
  }

  ngAfterViewInit() {
    this.fullOffset = this.track.nativeElement.offsetWidth;
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initLayersObserver () {
    this.selectedChapterCurrentLayers$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe((currentLayers) => {
      this.currentLayers = currentLayers;
    });
  }

  initIndexPositionObserver () {
    combineLatest(
      this.selectedChapterCurrentLayersIndex$,
      this.selectedChapterCurrentLayersPosition$
    ).pipe(
      filter(([currentIndex, currentPosition]) => {
        return !isNil(currentIndex) && !isNil(currentPosition);
      }),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(([currentIndex, currentPosition]) => {

        this.currentLayerName = this.currentLayers[currentIndex].name;

        const fraction = this.visibilityLayersService.indexPositionToFraction(
          currentIndex, 
          currentPosition
        );
        
        this.setSliderBar(fraction);
        this.setSliderHandle(fraction);
    });
  }

  initDragMoveObserver () {
    this.dragMove.pipe(
      auditTime(20),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(() => this.handleAuditedDrag());
  }

  handleDragEnded() {
    const fraction = this.calculateHandleFraction();
    const index = this.visibilityLayersService.fractionToIndex(fraction);
    const position = this.visibilityLayersService.fractionToPosition(fraction, index);

    this.visibilityLayersService.recordLayerIndexPosition(index, position);
  }

  handleDragMoved(event: DragEvent) {
    this.dragMove.next(event);
  }

  handleAuditedDrag() {
    const fraction = this.calculateHandleFraction();
    const index = this.visibilityLayersService.fractionToIndex(fraction);
    const position = this.visibilityLayersService.fractionToPosition(fraction, index);

    this.currentLayerName = this.currentLayers[index].name;

    // While dragging, synch the scene directly through engine for performance
    this.visibilityLayersService.synchronizeScene(index, position, false);
    this.setSliderBar(fraction);
  }

  handleTrackClick(event: MouseEvent) {
    const fraction = event.offsetX / this.fullOffset;
    const index = this.visibilityLayersService.fractionToIndex(fraction);
    const position = this.visibilityLayersService.fractionToPosition(fraction, index);

    this.visibilityLayersService.recordLayerIndexPosition(index, position);
  }

  private calculateHandleFraction(): number {
    const sliderRect = this.slider.nativeElement.getBoundingClientRect();
    const handleRect = this.handle.nativeElement.getBoundingClientRect();
    const offset = handleRect.left - sliderRect.left;

    return offset / this.fullOffset;
  }

  private setSliderBar(fraction: number) {
    this.renderer.setStyle(this.bar.nativeElement, 'width', `${fraction * 100}%`);
  }

  private setSliderHandle(fraction: number) {
    this.dragPosition = {x: fraction * this.fullOffset, y: this.dragPosition.y};
    this.changeDetectorRef.markForCheck();
  }

}
